import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import InputField from '../../components/common/InputField/InputField';
import styles from './DocumentsStoragePage.module.scss';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { documentService } from '../../services/documentService';
import { xlsxDocumentService, XlsxDocument } from '../../services/xlsxDocumentService';
import { API_BASE_URL, BACKEND_BASE_URL } from '../../utils/constants';
import { formatDateDMY } from '../../utils/helpers';

interface Document {
  id: number;
  name: string;
  file_url: string;
  file_type: string;
  uploaded_by: string;
  created_at: string;
  updated_at: string;
}

const DocumentsStoragePage: React.FC = () => {
  const { auth } = useAuth();
  const [documents, setDocuments] = useState<Document[] | null>(null);
  const [xlsxDocuments, setXlsxDocuments] = useState<XlsxDocument[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLoading, setUploadingLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showXlsxUploadModal, setShowXlsxUploadModal] = useState(false);
  const [showPdfUploadModal, setShowPdfUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [documentName, setDocumentName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [filteredXlsxDocuments, setFilteredXlsxDocuments] = useState<XlsxDocument[]>([]);
  const [activeTab, setActiveTab] = useState<'pdf' | 'xlsx' | 'duplicate'>('xlsx');
  const [uploadMode, setUploadMode] = useState<'single' | 'multiple'>('multiple');
  const [totalXlsxCount, setTotalXlsxCount] = useState<number>(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [filteredDuplicateDocuments, setFilteredDuplicateDocuments] = useState<XlsxDocument[]>([]);
  const [duplicateDocuments, setDuplicateDocuments] = useState<XlsxDocument[] | null>(null);

  // Check if user has document management permissions
  // const canManageDocuments = auth.user?.role === 'admin' || 
  //   auth.user?.permissions?.canManageDocuments;

  useEffect(() => {
    // Fetch total count when component loads
    fetchTotalCount();
    fetchXlsxDocumentsDuplicate();
  }, []);

  // Remove automatic search on typing - now only triggered by button click
  // useEffect(() => {
  //   // Only fetch data when search term is provided
  //   if (searchTerm.trim()) {
  //     setHasSearched(true);
  //     fetchXlsxDocuments();
  //   } else {
  //     setHasSearched(false);
  //     setXlsxDocuments(null);
  //     setFilteredXlsxDocuments([]);
  //     // setTotalXlsxCount(0);
  //   }
  // }, [searchTerm]);

  // Filter documents based on search term
  useEffect(() => {
    console.log('Filter effect triggered - documents:', documents, 'searchTerm:', searchTerm);
    
    if (!documents) {
      console.log('No documents, setting empty filtered array');
      setFilteredDocuments([]);
      return;
    }
    
    const filtered = documents.filter(doc => {
      // Validate that doc is a proper object with required properties
      if (!doc || typeof doc !== 'object') {
        console.log('Invalid doc object:', doc);
        return false;
      }
      if (!doc.name) {
        console.log('Doc missing name or original_name:', doc);
        return false;
      }
      
      return doc.name.toLowerCase().includes(searchTerm.toLowerCase());
    });
    
    console.log('Filtered documents:', filtered);
    setFilteredDocuments(filtered);
  }, [searchTerm, documents]);

  // Filter XLSX documents based on search term
  useEffect(() => {
    if (!xlsxDocuments) {
      setFilteredXlsxDocuments([]);
      return;
    }
    
    const filtered = xlsxDocuments.filter(doc => {
      if (!doc || typeof doc !== 'object') {
        return false;
      }
      
      return doc.document_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
             doc.document_details.toLowerCase().includes(searchTerm.toLowerCase());
    });
    
    setFilteredXlsxDocuments(filtered);
  }, [searchTerm, xlsxDocuments]);
  
  useEffect(() => {
    if (!duplicateDocuments) {
      setFilteredDuplicateDocuments([]);
      return;
    }
    
    const filtered = duplicateDocuments.filter(doc => {
      if (!doc || typeof doc !== 'object') {
        return false;
      }
      
      return doc.document_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
             doc.document_details.toLowerCase().includes(searchTerm.toLowerCase());
    });
    
    setFilteredDuplicateDocuments(filtered);
  }, [searchTerm, duplicateDocuments]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      console.log('Fetching documents...');
      const response = await documentService.getDocuments();
      console.log('API Response:', response);
      
      // Validate and filter out invalid documents
      const validDocuments = response.filter(doc => {
        const isValid = doc && 
               typeof doc === 'object' && 
               doc.id && 
               doc.name && 
               doc.original_name &&
               doc.file_url &&
               doc.file_size !== undefined &&
               doc.file_type;
        
        if (!isValid) {
          console.log('Invalid document found:', doc);
        }
        
        return isValid;
      });
      
      console.log('Valid documents:', validDocuments);
      setDocuments(validDocuments);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
      setDocuments([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchXlsxDocuments = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await xlsxDocumentService.getXlsxDocuments(searchTerm);
      setXlsxDocuments(response);
      setFilteredXlsxDocuments(response);
      // setTotalXlsxCount(response.length); // Count of search results
    } catch (error) {
      console.error('Error fetching XLSX documents:', error);
      toast.error('Failed to load XLSX documents');
      setXlsxDocuments([]);
      setFilteredXlsxDocuments([]);
      // setTotalXlsxCount(0);
    } finally {
      setIsSearching(false);
    }
  };
  
  const fetchXlsxDocumentsDuplicate = async () => {
    setLoadingDuplicates(true);
    try {
      const response = await xlsxDocumentService.getXlsxDocumentsDuplicate();
      setDuplicateDocuments(response);
      setFilteredDuplicateDocuments(response);
    } catch (error) {
      console.error('Error fetching duplicate XLSX documents:', error);
      toast.error('Failed to load duplicate XLSX documents');
      setDuplicateDocuments([]);
      setFilteredDuplicateDocuments([]);
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search');
      return;
    }
    setHasSearched(true);
    fetchXlsxDocuments();
  };

  const fetchTotalCount = async () => {
    try {
      const response = await xlsxDocumentService.getXlsxDocumentsCount();
      console.log("response.data32113::::::::", response.total_count);
      setTotalXlsxCount(response.total_count);
    } catch (error) {
      console.error('Error fetching total count:', error);
      // setTotalXlsxCount(0);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-fill document name with file name (without extension)
      const fileName = file.name.replace(/\.[^/.]+$/, '');
      console.log("fileName::::::::", fileName);
      setDocumentName(fileName);
    }
  };

  const handleMultipleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(files);
    }
  };

  const handleMultiplePdfUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select PDF files to upload');
      return;
    }
    setUploadingLoading(true);
    setUploading(true);
    try {
      const formData = new FormData();
      
      // Add all files to form data
      selectedFiles.forEach((file, index) => {
        formData.append('files', file);
        // Add names if provided, otherwise use file names
        const fileName = file.name.replace(/\.[^/.]+$/, '');
        console.log("fileName12334::::::::", fileName);
        formData.append('names', fileName);
      });
      
      const response = await xlsxDocumentService.uploadMultiplePdfDocuments(formData);
      
      toast.success(`Successfully uploaded ${response.totalUploaded} PDF files to documents folder`);
      if (response.totalErrors > 0) {
        toast.warning(`${response.totalErrors} files failed to upload`);
      }
      
      setShowPdfUploadModal(false);
      setSelectedFiles([]);
      // Note: We don't call fetchDocuments() since files are only stored in folder, not database
    } catch (error) {
      console.error('Error uploading multiple PDF documents:', error);
      toast.error('Failed to upload PDF documents');
    } finally {
      setUploading(false);
      setUploadingLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !documentName.trim()) {
      toast.error('Please select a file and enter a document name');
      return;
    }

    setUploading(true);
    setUploadingLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', documentName);
      const newDocument = await documentService.uploadDocument(formData);
      
      setDocuments(prev => [newDocument, ...(prev || [])]);
      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      setSelectedFile(null);
      setDocumentName('');
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      setUploadingLoading(false);
    }
  };

  const handleXlsxUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select an XLSX file');
      return;
    }

    setUploading(true);
    setUploadingLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await xlsxDocumentService.uploadXlsxDocument(formData);
      
      toast.success(`Successfully processed ${response.totalRecords} records from XLSX file and stored XLSX file in documents folder`);
      setShowXlsxUploadModal(false);
      setSelectedFile(null);
      fetchXlsxDocuments();
    } catch (error) {
      console.error('Error uploading XLSX document:', error);
      toast.error('Failed to upload XLSX document');
    } finally {
      setUploading(false);
      setUploadingLoading(false);
    }
  };

  const handlePdfUpload = async () => {
    if (!selectedFile || !documentName.trim()) {
      toast.error('Please select a PDF file and enter a document name');
      return;
    }

    setUploading(true);
    setUploadingLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', documentName);
      const response = await xlsxDocumentService.uploadPdfDocument(formData);
      
      toast.success('PDF document uploaded successfully to documents folder');
      console.log('PDF file stored at:', response.data.url);
      setShowPdfUploadModal(false);
      setSelectedFile(null);
      setDocumentName('');
      // Note: We don't call fetchDocuments() since files are only stored in folder, not database
    } catch (error) {
      console.error('Error uploading PDF document:', error);
      toast.error('Failed to upload PDF document');
    } finally {
      setUploading(false);
      setUploadingLoading(false);
    }
  };

  const handleDeleteXlsxDocument = async (doc: XlsxDocument) => {
    if (!doc || !doc.id) return;

    try {
      await xlsxDocumentService.deleteXlsxDocument(doc.id);
      setXlsxDocuments(prev => prev ? prev.filter(d => d.id !== doc.id) : []);
      setDuplicateDocuments(prev => prev ? prev.filter(d => d.document_name !== doc.document_name) : []);
      toast.success('XLSX document deleted successfully');
    } catch (error) {
      console.error('Error deleting XLSX document:', error);
      toast.error('Failed to delete XLSX document');
    }
  };

  const handleDeleteClick = (document: Document) => {
    setDocumentToDelete(document);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    
    try {
      await documentService.deleteDocument(documentToDelete.id);
      setDocuments(prev => prev ? prev.filter(doc => doc.id !== documentToDelete.id) : []);
      toast.success('Document deleted successfully');
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${doc.id}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

    const getFileIcon = (fileType: string, size = 24) => {
      if (fileType.includes('pdf')) {
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#dc2626" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 2v6h6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <text x="12" y="16" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">PDF</text>
          </svg>
        );
      }
      
      if (fileType.includes('word') || fileType.includes('document')) {
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#2563eb" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 2v6h6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <text x="12" y="16" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">DOC</text>
          </svg>
        );
      }
      
      if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#16a34a" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 2v6h6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <text x="12" y="16" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">XLS</text>
          </svg>
        );
      }
      
      if (fileType.includes('image')) {
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#7c3aed" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 2v6h6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="10" cy="12" r="1" fill="white"/>
            <path d="M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      }
      
      // Default file icon
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#6b7280" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 2v6h6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 13H8" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M16 17H8" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M10 9H8" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    };

  // if (!canManageDocuments) {
  //   return (
  //     <div className={styles.errorContainer}>
  //       <h2>Access Denied</h2>
  //       <p>You don't have permission to access documents.</p>
  //     </div>
  //   );
  // }

  return (
    <div className={styles.container}>
    {uploadingLoading && (
  <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
    <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600 mb-3"></div>
      <p className="text-gray-700 font-medium">Uploading document, please wait...</p>
    </div>
  </div>
)}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>CC Storage</h2>
          <p>Manage and organize your company documents</p>
        </div>
        <div className={styles.headerRight}>
          {/* Upload buttons - only for admin */}
          {/* {auth.user?.role === 'admin' && ( */}
            <div className={styles.uploadButtons}>
              <Button variant="primary" onClick={() => setShowXlsxUploadModal(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 13h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 17h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Upload XLSX
              </Button>
              <Button variant="primary" onClick={() => setShowPdfUploadModal(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 13h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 17h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Upload PDF
              </Button>
            </div>
            
          {/* )} */}
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        {/* <button
          className={`${styles.tab} ${activeTab === 'pdf' ? styles.active : ''}`}
          onClick={() => setActiveTab('pdf')}
        >
          PDF Documents ({filteredDocuments.length})
        </button> */}
        <button
          className={`${styles.tab} ${activeTab === 'xlsx' ? styles.active : ''}`}
          onClick={() => setActiveTab('xlsx')}
        >
          XLSX Documents {auth.user?.role === 'admin' ? '(' + totalXlsxCount + ')' : ''}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'duplicate' ? styles.active : ''}`}
          onClick={() => setActiveTab('duplicate')}
        >
          Duplicate Documents
        </button>
      </div>
 {activeTab === 'xlsx' && (
        <>
      <div className={styles.searchSection}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ width: 380 }}>
            <InputField
              name="search"
              placeholder="Search XLSX documents to load data..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
  <Button 
    variant="primary" 
    onClick={handleSearch}
    disabled={isSearching}
  >
    {isSearching ? 'Searching...' : 'Search'}
  </Button>
</div>
        </div>
      </div>

      <div className={styles.documentsList}>
        {isSearching ? (
          <div className={styles.loading}>
            <p>Searching documents...</p>
          </div>
        ) : !hasSearched ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>🔍</div>
            <h3>Search to view XLSX documents</h3>
            <p>Enter a search term above and click Search to find and display XLSX documents from the database</p>
          </div>
        ) : filteredXlsxDocuments.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📁</div>
            <h3>No XLSX documents found</h3>
            <p>No documents match your search term "{searchTerm}"</p>
          </div>
        ) : (
          <div className={styles.documentsTable}>
            <div className={styles.resultsInfo}>
              <p>Found {filteredXlsxDocuments.length} document(s) for "{searchTerm}"</p>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Details</th>
                  <th>Date</th>
                  {auth.user?.role === 'admin' && (
                  <th>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredXlsxDocuments.map((doc) => {
                  // Additional safety check for rendering
                  if (!doc || !doc.id || !doc.document_name) {
                    return null; // Skip invalid documents
                  }
                  
                  return (
                    <tr key={doc.id}>
                      <td className={styles.fileTypeCell}>
                        <span className={styles.fileIcon}>{getFileIcon('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')}</span>
                      </td>
                      <td className={styles.nameCell}>
                        <strong 
                          
                          onClick={() => window.open(`${BACKEND_BASE_URL}${doc.file_url}`, '_blank')}
                        >
                          {doc.document_name}
                        </strong>
                      </td>
                      <td className={styles.detailsCell}>
                        <div
  className={styles.documentDetails}
  style={{ color: '#000000' }}
>
  <strong 
  style={{ cursor: 'pointer', color: '#2563eb', textDecoration: 'underline' }}
  onClick={() => window.open(`${BACKEND_BASE_URL}${doc.file_url}`, '_blank')}>{doc.document_details.replace("R/", "")}</strong>
</div>
                      </td>
                      <td className={styles.dateCell}>
                        <small>{doc.document_date ? formatDateDMY(doc.document_date) : 'N/A'}</small>
                      </td>
                      {auth.user?.role === 'admin' && (
                      <td className={styles.actionsCell}>
                        <div className={styles.tableActions}>
                          {/* View button - always visible */}
                          {/* <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => window.open(`${BACKEND_BASE_URL}${doc.file_url}`, '_blank')}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </Button> */}
                          {/* Download button - always visible */}
                          {/* <Button
                            variant="secondary"
                            size="sm"
                            onClick={async () => {
                              try {
                                const response = await fetch(`${BACKEND_BASE_URL}${doc.file_url}`, {
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                  }
                                });
                                
                                if (!response.ok) {
                                  throw new Error('Download failed');
                                }
                                
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${doc.document_name}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                              } catch (error) {
                                console.error('Error downloading document:', error);
                                toast.error('Failed to download document');
                              }
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="7,10 12,15 17,10"></polyline>
                              <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                          </Button> */}
                          {/* Delete button - only for admin */}
                          
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeleteXlsxDocument(doc)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3,6 5,6 21,6"></polyline>
                                <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </Button>
                          
                        </div>
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
       </>
      )}

      {activeTab === 'duplicate' && (
        <>
          <div className={styles.searchSection}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <div style={{ width: 380 }}>
                <InputField
                  name="duplicateSearch"
                  placeholder="Filter duplicate documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
            </div>
          </div>

          <div className={styles.documentsList}>
            {loadingDuplicates ? (
              <div className={styles.loading}>
                <p>Loading duplicate documents...</p>
              </div>
            ) : !duplicateDocuments || duplicateDocuments.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>📋</div>
                <h3>No duplicate documents found</h3>
                <p>There are currently no duplicate XLSX documents in the system</p>
              </div>
            ) : filteredDuplicateDocuments.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>🔍</div>
                <h3>No matches found</h3>
                <p>No duplicate documents match your search term "{searchTerm}"</p>
              </div>
            ) : (
              <div className={styles.documentsTable}>
                <div className={styles.resultsInfo}>
                  <p>Found {filteredDuplicateDocuments.length} duplicate document(s)</p>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Details</th>
                      <th>Date</th>
                      {auth.user?.role === 'admin' && (
                        <th>Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDuplicateDocuments.map((doc) => {
                      if (!doc || !doc.id || !doc.document_name) {
                        return null;
                      }
                      
                      return (
                        <tr key={doc.id}>
                          <td className={styles.fileTypeCell}>
                            <span className={styles.fileIcon}>{getFileIcon('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')}</span>
                          </td>
                          <td className={styles.nameCell}>
                            <strong 
                              style={{ cursor: 'pointer', color: '#2563eb', textDecoration: 'underline' }}
                              onClick={() => window.open(`${BACKEND_BASE_URL}${doc.file_url}`, '_blank')}
                            >
                              {doc.document_name}
                            </strong>
                          </td>
                          <td className={styles.detailsCell}>
                            <div className={styles.documentDetails}>
                              <small>{doc.document_details}</small>
                            </div>
                          </td>
                          <td className={styles.dateCell}>
                            {doc.document_date ? formatDateDMY(doc.document_date) : 'N/A'}
                          </td>
                          {auth.user?.role === 'admin' && (
                            <td className={styles.actionsCell}>
                              <div className={styles.tableActions}>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleDeleteXlsxDocument(doc)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3,6 5,6 21,6"></polyline>
                                    <path d="m19,6v14a2,2 0,0,1 -2,2H7a2,2 0,0,1 -2,-2V6m3,0V4a2,2 0,0,1 2,-2h4a2,2 0,0,1 2,2v2"></path>
                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                  </svg>
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Upload Modal - only for admin */}
      {/* {auth.user?.role === 'admin' && ( */}
        <Modal
          isOpen={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setSelectedFile(null);
            setDocumentName('');
          }}
          title="Upload Document"
        >
          <div className={styles.uploadForm}>
            <div className={styles.fileUpload}>
              <label htmlFor="file-input" className={styles.fileInputLabel}>
                {selectedFile ? (
                  <div className={styles.fileSelected}>
                    <span className={styles.fileIcon}>📄</span>
                    <span className={styles.fileName}>{selectedFile.name}</span>
                    <span className={styles.fileSize}>{formatFileSize(selectedFile.size)}</span>
                  </div>
                ) : (
                  <div className={styles.fileDropZone}>
                    <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    <p>Click to select a file or drag and drop</p>
                    <p className={styles.fileTypes}>Supports: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG</p>
                  </div>
                )}
              </label>
              <input
                id="file-input"
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
              />
            </div>

            <InputField
              name="documentName"
              label="Document Name"
              type="text"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              placeholder="Enter a descriptive name for this document"
              required
            />
          </div>

          <div className={styles.modalActions}>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!selectedFile || !documentName.trim() || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowUploadModal(false);
                setSelectedFile(null);
                setDocumentName('');
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal>
      {/* )} */}

      {/* XLSX Upload Modal - only for admin */}
      {/* {auth.user?.role === 'admin' && ( */}
        <Modal
          isOpen={showXlsxUploadModal}
          onClose={() => {
            setShowXlsxUploadModal(false);
            setSelectedFile(null);
            setDocumentName('');
          }}
          title="Upload XLSX Document"
        >
          <div className={styles.uploadForm}>
            <div className={styles.fileUpload}>
              <label htmlFor="xlsx-file-input" className={styles.fileInputLabel}>
                {selectedFile ? (
                  <div className={styles.fileSelected}>
                    <span className={styles.fileIcon}>📄</span>
                    <span className={styles.fileName}>{selectedFile.name}</span>
                    <span className={styles.fileSize}>{formatFileSize(selectedFile.size)}</span>
                  </div>
                ) : (
                  <div className={styles.fileDropZone}>
                    <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    <p>Click to select an XLSX file or drag and drop</p>
                    <p className={styles.fileTypes}>Supports: XLSX</p>
                  </div>
                )}
              </label>
              <input
                id="xlsx-file-input"
                type="file"
                onChange={handleFileSelect}
                accept=".xlsx"
                style={{ display: 'none' }}
              />
            </div>

            <InputField
              name="xlsxDocumentName"
              label="Document Name"
              type="text"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              placeholder="Enter a descriptive name for this XLSX document"
              required
            />
          </div>

          <div className={styles.modalActions}>
            <Button
              variant="primary"
              onClick={handleXlsxUpload}
              disabled={!selectedFile || !documentName.trim() || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload XLSX Document'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowXlsxUploadModal(false);
                setSelectedFile(null);
                setDocumentName('');
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal>
      {/* )} */}

      {/* PDF Upload Modal - only for admin */}
      {/* {auth.user?.role === 'admin' && ( */}
        <Modal
          isOpen={showPdfUploadModal}
          onClose={() => {
            setShowPdfUploadModal(false);
            setSelectedFile(null);
            setSelectedFiles([]);
            setDocumentName('');
            setUploadMode('multiple');
          }}
          title="Upload PDF Document"
        >
          <div className={styles.uploadForm}>
            {/* Upload Mode Toggle */}
            {/* <div className={styles.uploadModeToggle}>
              <button
                type="button"
                className={`${styles.modeButton} ${uploadMode === 'single' ? styles.active : ''}`}
                onClick={() => setUploadMode('single')}
              >
                Single File
              </button>
              <button
                type="button"
                className={`${styles.modeButton} ${uploadMode === 'multiple' ? styles.active : ''}`}
                onClick={() => setUploadMode('multiple')}
              >
                Multiple Files
              </button>
            </div> */}

            <div className={styles.fileUpload}>
              {uploadMode === 'single' ? (
                <label htmlFor="pdf-file-input" className={styles.fileInputLabel}>
                  {selectedFile ? (
                    <div className={styles.fileSelected}>
                      <span className={styles.fileIcon}>📄</span>
                      <span className={styles.fileName}>{selectedFile.name}</span>
                      <span className={styles.fileSize}>{formatFileSize(selectedFile.size)}</span>
                    </div>
                  ) : (
                    <div className={styles.fileDropZone}>
                      <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      <p>Click to select a PDF file or drag and drop</p>
                      <p className={styles.fileTypes}>Supports: PDF only</p>
                    </div>
                  )}
                </label>
              ) : (
                <label htmlFor="pdf-multiple-file-input" className={styles.fileInputLabel}>
                  {selectedFiles.length > 0 ? (
                    <div className={styles.filesSelected}>
                      <span className={styles.fileIcon}>📁</span>
                      <span className={styles.fileName}>{selectedFiles.length} PDF files selected</span>
                      <div className={styles.fileList}>
                        {selectedFiles.map((file, index) => (
                          <div key={index} className={styles.fileItem}>
                            <span>{file.name}</span>
                            <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.fileDropZone}>
                      <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      <p>Click to select multiple PDF files or drag and drop</p>
                      <p className={styles.fileTypes}>Supports: PDF files only (max 100 files)</p>
                    </div>
                  )}
                </label>
              )}
              
              {uploadMode === 'single' ? (
                <input
                  id="pdf-file-input"
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf"
                  style={{ display: 'none' }}
                />
              ) : (
                <input
                  id="pdf-multiple-file-input"
                  type="file"
                  onChange={handleMultipleFileSelect}
                  accept=".pdf"
                  multiple
                  style={{ display: 'none' }}
                />
              )}
            </div>

            {uploadMode === 'single' && (
              <InputField
                name="pdfDocumentName"
                label="Document Name"
                type="text"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                placeholder="Enter a descriptive name for this PDF document"
                required
              />
            )}
          </div>

          <div className={styles.modalActions}>
            <Button
              variant="primary"
              onClick={uploadMode === 'single' ? handlePdfUpload : handleMultiplePdfUpload}
              disabled={
                (uploadMode === 'single' && (!selectedFile || !documentName.trim())) ||
                (uploadMode === 'multiple' && selectedFiles.length === 0) ||
                uploading
              }
            >
              {uploading ? 'Uploading...' : `Upload ${uploadMode === 'single' ? 'PDF' : 'PDFs'}`}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowPdfUploadModal(false);
                setSelectedFile(null);
                setSelectedFiles([]);
                setDocumentName('');
                setUploadMode('multiple');
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal>
      {/* // )} */}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDocumentToDelete(null);
        }}
        title="Delete Document"
      >
        <div className={styles.deleteConfirmation}>
          <div className={styles.warningIcon}>⚠️</div>
          <h3>Are you sure you want to delete this document?</h3>
          {documentToDelete && (
            <div className={styles.documentInfo}>
              <p><strong>Name:</strong> {documentToDelete.name}</p>
            </div>
          )}
          <p className={styles.warningText}>
            This action cannot be undone. The document will be permanently deleted from the server.
          </p>
        </div>

        <div className={styles.modalActions}>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
          >
            Delete Document
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setDocumentToDelete(null);
            }}
          >
            Cancel
          </Button>
        </div>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default DocumentsStoragePage; 
